import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import { logAuth } from '../utils/authDebug';
import { redirectAfterAuth } from '../utils/postAuthRedirect';
import { resolveOauthError } from '../utils/resolveOauthError';
import { resolveStudentAuthGates } from '../utils/authSessionGates';
import {
  getMusicStyleSelectionGate,
  savePreferredMusicStyles,
} from '../utils/musicStylePreferences';
import {
  saveStudentProfile,
  type ProfileCompletion,
  type StudentProfileFields,
} from '../utils/profileCompletion';

type OAuthBanner = {
  severity: 'error' | 'success' | 'info';
  message: string;
};

export function useOAuthReturn(enabled: boolean) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [banner, setBanner] = useState<OAuthBanner | null>(null);
  const [continuing, setContinuing] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState<ProfileCompletion | null>(null);
  const [musicStyleUserId, setMusicStyleUserId] = useState<string | null>(null);

  const applyStudentAuthGates = async (userId: string, role: string) => {
    const gate = await resolveStudentAuthGates(userId, role);

    if (gate.action === 'showProfile') {
      setProfileCompletion(gate.profile);
      return;
    }

    if (gate.action === 'showMusicStyles') {
      setMusicStyleUserId(gate.userId);
      return;
    }

    await redirectAfterAuth(navigate, new URLSearchParams(location.search));
  };

  const continueAfterAuthentication = async () => {
    const meRes = await fetch(getApiUrl('/api/v1/auth/me'), { credentials: 'include' });
    const meJson = await meRes.json().catch(() => ({}));

    if (meRes.ok && meJson?.success) {
      await applyStudentAuthGates(meJson.data?.userId, meJson.data?.role);
      return;
    }

    await redirectAfterAuth(navigate, new URLSearchParams(location.search));
  };

  useEffect(() => {
    if (!enabled) return;

    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const message = params.get('message');
    const oauthComplete = params.get('oauth') === 'complete';

    const clearQuery = () => {
      if (location.search) {
        navigate(location.pathname, { replace: true });
      }
    };

    if (error) {
      setBanner({
        severity: 'error',
        message: resolveOauthError(t, error, message),
      });
      clearQuery();
      return;
    }

    if (!oauthComplete) {
      return;
    }

    let cancelled = false;

    const continueIfAuthenticated = async () => {
      setContinuing(true);

      try {
        const meRes = await fetch(getApiUrl('/api/v1/auth/me'), { credentials: 'include' });
        const meJson = await meRes.json().catch(() => ({}));
        logAuth('oauth return /auth/me', {
          oauthComplete,
          ok: meRes.ok,
          status: meRes.status,
          success: meJson?.success,
          role: meJson?.data?.role,
        });

        if (cancelled) return;

        if (meRes.ok && meJson?.success) {
          await applyStudentAuthGates(meJson.data?.userId, meJson.data?.role);
          return;
        }

        setBanner({
          severity: 'error',
          message: t('auth.googleSignInFailed'),
        });
        clearQuery();
      } catch {
        if (!cancelled) {
          setBanner({
            severity: 'error',
            message: t('auth.googleSignInFailed'),
          });
          clearQuery();
        }
      } finally {
        if (!cancelled) {
          setContinuing(false);
        }
      }
    };

    void continueIfAuthenticated();

    return () => {
      cancelled = true;
    };
  }, [enabled, location.pathname, location.search, navigate, t]);

  const completeProfile = async (fields: StudentProfileFields) => {
    if (!profileCompletion) return;

    await saveStudentProfile(profileCompletion, fields);

    const userId = profileCompletion.userId;
    setProfileCompletion(null);

    const needsMusicStyles = await getMusicStyleSelectionGate(userId, 'Student');
    if (needsMusicStyles) {
      setMusicStyleUserId(needsMusicStyles);
      return;
    }

    await redirectAfterAuth(navigate, new URLSearchParams(location.search));
  };

  const completeMusicStyles = async (styles: string[]) => {
    if (!musicStyleUserId) return;

    await savePreferredMusicStyles(musicStyleUserId, styles);
    setMusicStyleUserId(null);
    await redirectAfterAuth(navigate, new URLSearchParams(location.search));
  };

  return {
    banner,
    continuing,
    profileCompletion,
    musicStyleUserId,
    completeProfile,
    completeMusicStyles,
    continueAfterAuthentication,
  };
}
